import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import useMember from "@/hooks/useMember";
import { joinWaitlist } from "@/lib/waitlist";
import PhoneInput from "@/components/base/PhoneInput";

const DEFAULT_TITLE = "Em breve, novos encontros";
const DEFAULT_MESSAGE =
  "O nosso último encontro foi especial e deixou saudade. Já estamos preparando com carinho o próximo, para seguirmos juntas nessa caminhada de fé, apoio e propósito. Acompanhe nossas redes para saber a data em primeira mão.";

interface RecentEvent {
  slug: string;
}

export default function UpcomingEventsWaitingState() {
  const { member, loading: memberLoading } = useMember();

  const [title, setTitle] = useState(DEFAULT_TITLE);
  const [message, setMessage] = useState(DEFAULT_MESSAGE);
  const [recentEvent, setRecentEvent] = useState<RecentEvent | null>(null);

  // Fluxo membro logado
  const [alreadyInList, setAlreadyInList] = useState<boolean | null>(null);
  const [memberJoining, setMemberJoining] = useState(false);
  const [memberJoined, setMemberJoined] = useState(false);

  // Fluxo visitante
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [countryCode, setCountryCode] = useState("+1");
  const [submitting, setSubmitting] = useState(false);
  const [formStatus, setFormStatus] = useState<"idle" | "success" | "already" | "error">("idle");
  const [formError, setFormError] = useState("");

  useEffect(() => {
    let active = true;

    supabase
      .from("upcoming_events_waiting_state")
      .select("title, message")
      .eq("id", 1)
      .maybeSingle()
      .then(({ data, error }) => {
        if (active && !error && data) {
          setTitle(data.title || DEFAULT_TITLE);
          setMessage(data.message || DEFAULT_MESSAGE);
        }
      });

    supabase
      .from("events")
      .select("slug")
      .eq("status", "active")
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle()
      .then(({ data }) => {
        if (active && data) {
          setRecentEvent(data as RecentEvent);
        }
      });

    return () => {
      active = false;
    };
  }, []);

  async function handleMemberJoin() {
    if (!member) return;
    setMemberJoining(true);
    try {
      const result = await joinWaitlist({
        name: member.full_name,
        email: member.email,
        whatsapp: member.whatsapp,
        countryCode: member.country_code,
      });
      if (result.status === "already") {
        setAlreadyInList(true);
      } else {
        setMemberJoined(true);
      }
    } catch {
      setMemberJoined(false);
    } finally {
      setMemberJoining(false);
    }
  }

  async function handleVisitorSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSubmitting(true);
    setFormStatus("idle");
    setFormError("");
    try {
      const result = await joinWaitlist({ name, email, whatsapp, countryCode });
      if (result.status === "already") {
        setFormStatus("already");
      } else {
        setFormStatus("success");
        setName("");
        setEmail("");
        setWhatsapp("");
        setCountryCode("+1");
      }
    } catch {
      setFormStatus("error");
      setFormError("Não foi possível salvar seu contato. Tente novamente.");
    } finally {
      setSubmitting(false);
    }
  }

  // Título que começa com "Encontro" vira cursiva + letra de inscrição (mesmo estilo da página do evento)
  const tituloPartes = title.match(/^Encontro\s+(.+)$/i);

  return (
    <div className="max-w-3xl mx-auto">
      {/* Banner "save the date" do próximo encontro (10/14/2026). Título e texto vêm de upcoming_events_waiting_state,
          então dá para trocar pelo painel admin sem publicar o site. */}
      {/* Só cores da paleta do site: brown #5C3A21 → primary-dark #A85A3F, detalhes em nude #E8D5C0 e beige #FDF5EC */}
      <div className="relative overflow-hidden rounded-t-2xl bg-gradient-to-br from-brown via-[#7A4A2E] to-primary-dark px-6 py-12 md:px-12 md:py-16 text-center">
        <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-[520px] h-[260px] rounded-full bg-nude/15 blur-3xl pointer-events-none" />
        <div className="absolute inset-3 md:inset-4 border border-nude/30 rounded-xl pointer-events-none" />

        <div className="relative">
          <img src="/img/logo-entre-elas.jpg" alt="" className="w-14 h-14 rounded-full mx-auto mb-5 ring-2 ring-nude/50" />
          <p className="font-['Montserrat'] font-medium text-[10px] md:text-[11px] tracking-[0.38em] uppercase text-nude">
            Save the date · Próximo encontro
          </p>
          {tituloPartes && (
            <p className="font-['Great_Vibes'] text-5xl md:text-6xl text-nude leading-none mt-5">Encontro</p>
          )}
          <h3 className={`font-['Cinzel'] font-medium uppercase text-beige tracking-[0.12em] leading-tight text-2xl md:text-4xl ${tituloPartes ? "mt-1" : "mt-5"}`}>
            {tituloPartes ? tituloPartes[1] : title}
          </h3>
          <div className="flex items-center justify-center gap-3.5 mt-5 text-nude" aria-hidden="true">
            <span className="w-14 md:w-20 h-px bg-current opacity-60" />
            <i className="ri-heart-fill text-xs" />
            <span className="w-14 md:w-20 h-px bg-current opacity-60" />
          </div>
          <p className="font-body text-sm md:text-base text-beige/85 leading-relaxed max-w-xl mx-auto mt-6">
            {message}
          </p>
        </div>
      </div>

      <div className="relative overflow-hidden rounded-b-2xl bg-gradient-to-br from-beige via-beige/90 to-nude/40 border border-t-0 border-nude/50 px-6 py-10 md:px-12 md:py-12 text-center">
        <div className="relative">
          <a
            href="https://www.instagram.com/entreelas.community_/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-primary text-white font-body text-sm tracking-wide uppercase transition-all duration-300 hover:bg-primary-dark whitespace-nowrap cursor-pointer"
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-instagram-line text-lg" />
            </div>
            Seguir @entreelas.community_
          </a>

          {/* Quero ser avisada */}
          <div className="mt-10 pt-8 border-t border-nude/60">
            <p className="font-display text-lg text-brown mb-2">Quero ser avisada</p>

            {memberLoading ? (
              <div className="flex items-center justify-center py-4">
                <div className="w-5 h-5 border-2 border-primary/20 border-t-primary rounded-full animate-spin" />
              </div>
            ) : member ? (
              // --- Membro logado: um único botão ---
              <div className="max-w-md mx-auto">
                <p className="font-body text-sm text-brown/70 leading-relaxed mb-5">
                  Olá, <strong className="text-primary">{member.full_name}</strong>! Quer ser
                  avisada do próximo encontro?
                </p>

                {memberJoined ? (
                  <div className="px-5 py-4 rounded-xl bg-primary/10 border border-primary/20">
                    <p className="font-body text-sm text-primary flex items-center justify-center gap-2">
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-checkbox-circle-line text-base" />
                      </div>
                      Tudo certo! Você será avisada sobre o próximo encontro.
                    </p>
                  </div>
                ) : alreadyInList === true ? (
                  <div className="px-5 py-4 rounded-xl bg-primary/10 border border-primary/20">
                    <p className="font-body text-sm text-primary flex items-center justify-center gap-2">
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-heart-3-line text-base" />
                      </div>
                      Você já está na nossa lista!
                    </p>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={handleMemberJoin}
                    disabled={memberJoining}
                    className="px-8 py-4 rounded-full bg-brown text-beige font-body text-sm tracking-wide uppercase transition-all duration-300 hover:bg-brown-light disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                  >
                    {memberJoining ? "Salvando..." : "Quero ser avisada"}
                  </button>
                )}
              </div>
            ) : (
              // --- Visitante: formulário curto ---
              <div className="max-w-md mx-auto">
                <p className="font-body text-sm text-brown/60 mb-5">
                  Deixe seu contato e avisaremos você assim que a próxima data for anunciada.
                </p>

                {formStatus === "success" ? (
                  <div className="px-5 py-4 rounded-xl bg-primary/10 border border-primary/20">
                    <p className="font-body text-sm text-primary flex items-center justify-center gap-2">
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-checkbox-circle-line text-base" />
                      </div>
                      Obrigada! Você será avisada sobre o próximo encontro.
                    </p>
                  </div>
                ) : formStatus === "already" ? (
                  <div className="px-5 py-4 rounded-xl bg-primary/10 border border-primary/20">
                    <p className="font-body text-sm text-primary flex items-center justify-center gap-2">
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-heart-3-line text-base" />
                      </div>
                      Você já está na nossa lista! Em breve avisaremos a data.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleVisitorSubmit} className="space-y-3 text-left">
                    <div className="flex flex-col sm:flex-row gap-3">
                      <input
                        type="text"
                        name="name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Seu nome"
                        required
                        className="flex-1 px-5 py-3.5 rounded-full border border-nude bg-white font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors"
                      />
                      <input
                        type="email"
                        name="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="seu@email.com"
                        required
                        className="flex-1 px-5 py-3.5 rounded-full border border-nude bg-white font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors"
                      />
                    </div>
                    <PhoneInput
                      countryCode={countryCode}
                      whatsapp={whatsapp}
                      onChange={({ countryCode: cc, whatsapp: w }) => {
                        setCountryCode(cc);
                        setWhatsapp(w);
                      }}
                      placeholder="WhatsApp (opcional)"
                    />
                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full px-6 py-3.5 rounded-full bg-brown text-beige font-body text-sm tracking-wide uppercase transition-all duration-300 hover:bg-brown-light disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                    >
                      {submitting ? "Salvando..." : "Quero ser avisada"}
                    </button>
                  </form>
                )}

                {formStatus === "error" && (
                  <p className="mt-3 font-body text-sm text-red-600">{formError}</p>
                )}
              </div>
            )}
          </div>

          {/* Gallery link */}
          {recentEvent && (
            <Link
              to={`/evento/${recentEvent.slug}`}
              className="mt-8 inline-flex items-center gap-1.5 font-body text-sm text-brown/60 hover:text-primary transition-colors cursor-pointer"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-camera-line text-sm" />
              </div>
              Veja como foi o nosso último encontro
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}